import { Badge, Spinner } from '@personachat/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { CornerDownLeft, KeyRound, Search, UserRound } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api/client';
import { lire } from '../api/call';
import { chercherEcrans } from '../layout/navigation';
import { LIBELLE_TYPE } from '../lib/labels';
import { useDebounced } from '../lib/useDebounced';

/** Recherche d'élèves et de clés à partir de 3 caractères (e-mail, nom, code de clé). */
const LONGUEUR_MIN_ELEVES = 3;

interface Resultat {
  id: string;
  kind: 'ecran' | 'eleve' | 'cle';
  titre: string;
  detail: string;
  chemin: string;
  badge?: string;
}

/**
 * Recherche globale (Cmd+K) : élèves (e-mail, nom, code de leur clé), clés
 * d'accès (code, cohorte) et écrans du back-office selon le rôle. Choisir une
 * élève ou une clé ouvre sa fiche.
 */
export function CommandPalette({
  open,
  onOpenChange,
  isSuperAdmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSuperAdmin: boolean;
}) {
  const navigate = useNavigate();
  const idListe = useId();
  const [requete, setRequete] = useState('');
  const [actif, setActif] = useState(0);
  const recherche = useDebounced(requete.trim(), 250);

  const eleves = useQuery({
    queryKey: ['palette', 'eleves', recherche],
    enabled: open && recherche.length >= LONGUEUR_MIN_ELEVES,
    staleTime: 30_000,
    queryFn: async () => (await lire(api.GET('/api/admin/users', { params: { query: { search: recherche, limit: 5 } } }))).users,
  });

  const cles = useQuery({
    queryKey: ['palette', 'cles', recherche],
    enabled: open && recherche.length >= LONGUEUR_MIN_ELEVES,
    staleTime: 30_000,
    queryFn: async () => (await lire(api.GET('/api/admin/access-keys', { params: { query: { search: recherche, limit: 3 } } }))).accessKeys,
  });

  const resultats = useMemo<Resultat[]>(() => {
    const ecrans: Resultat[] = chercherEcrans(requete, isSuperAdmin)
      .slice(0, 8)
      .map((e) => ({ id: `ecran-${e.id}`, kind: 'ecran', titre: e.label, detail: e.summary, chemin: e.path }));
    const assez = recherche.length >= LONGUEUR_MIN_ELEVES;
    const trouvees: Resultat[] = assez
      ? (eleves.data ?? []).map((u) => ({
          id: `eleve-${u.id}`,
          kind: 'eleve',
          titre: u.fullName ?? u.username ?? u.email,
          detail: u.email,
          chemin: `/eleves/${u.id}`,
          badge: u.isAdmin ? 'Admin' : LIBELLE_TYPE[u.userType],
        }))
      : [];
    const clesTrouvees: Resultat[] = assez
      ? (cles.data ?? []).map((k) => ({
          id: `cle-${k.id}`,
          kind: 'cle',
          titre: k.key,
          detail: k.description ?? 'Clé d’accès',
          chemin: `/cles-acces/${k.id}`,
          badge: LIBELLE_TYPE[k.keyType],
        }))
      : [];
    return [...trouvees, ...clesTrouvees, ...ecrans];
  }, [requete, recherche, isSuperAdmin, eleves.data, cles.data]);

  useEffect(() => setActif(0), [requete]);
  useEffect(() => {
    if (!open) setRequete('');
  }, [open]);

  const choisir = (r: Resultat | undefined) => {
    if (!r) return;
    onOpenChange(false);
    navigate(r.chemin);
  };

  const auClavier = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActif((i) => Math.min(i + 1, resultats.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActif((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choisir(resultats[actif]);
    }
  };

  const chargementEleves = recherche.length >= LONGUEUR_MIN_ELEVES && (eleves.isFetching || cles.isFetching);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="palette__overlay" />
        <Dialog.Content className="palette" aria-describedby={undefined}>
          <Dialog.Title className="sr-only">Recherche globale</Dialog.Title>
          <div className="palette__input-row">
            <Search size={16} aria-hidden="true" />
            <input
              className="palette__input"
              autoFocus
              placeholder="E-mail d’une élève, code de clé, nom d’écran…"
              value={requete}
              onChange={(e) => setRequete(e.target.value)}
              onKeyDown={auClavier}
              role="combobox"
              aria-expanded="true"
              aria-controls={idListe}
              aria-activedescendant={resultats[actif] ? `${idListe}-${resultats[actif].id}` : undefined}
              aria-label="Rechercher"
            />
            {chargementEleves && <Spinner label="Recherche des élèves" />}
          </div>

          {eleves.isError && recherche.length >= LONGUEUR_MIN_ELEVES && (
            <div className="palette__notice">Recherche d’élèves impossible : {(eleves.error as Error).message}</div>
          )}
          {requete.trim().length > 0 && requete.trim().length < LONGUEUR_MIN_ELEVES && (
            <div className="palette__notice">Tapez au moins 3 caractères pour chercher une élève.</div>
          )}

          <ul className="palette__list" id={idListe} role="listbox" aria-label="Résultats">
            {resultats.length === 0 && !chargementEleves && <li className="palette__empty">Aucun résultat.</li>}
            {resultats.map((r, i) => (
              <li
                key={r.id}
                id={`${idListe}-${r.id}`}
                role="option"
                aria-selected={i === actif}
                className={i === actif ? 'palette__item palette__item--active' : 'palette__item'}
                onMouseEnter={() => setActif(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choisir(r)}
              >
                <span className="palette__item-icon" aria-hidden="true">
                  {r.kind === 'eleve' ? <UserRound size={15} /> : r.kind === 'cle' ? <KeyRound size={15} /> : <CornerDownLeft size={15} />}
                </span>
                <span className="palette__item-text">
                  <span className="palette__item-title">{r.titre}</span>
                  <span className="palette__item-detail">{r.detail}</span>
                </span>
                {r.badge && <Badge>{r.badge}</Badge>}
                {r.kind === 'ecran' && <span className="palette__item-kind">Écran</span>}
              </li>
            ))}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
